'use client';

import { Container as ChakraContainer } from '@chakra-ui/react';
import type { ContainerProps as ChakraContainerProps } from '@chakra-ui/react';
import { forwardRef } from 'react';

export interface ContainerProps
  extends Omit<
    ChakraContainerProps,
    // Remove shorthand props
    | 'w'
    | 'h'
    | 'maxW'
    | 'minW'
    | 'p'
    | 'pt'
    | 'pr'
    | 'pb'
    | 'pl'
    | 'px'
    | 'py'
    | 'm'
    | 'mt'
    | 'mr'
    | 'mb'
    | 'ml'
    | 'mx'
    | 'my'
  > {
  // Container-specific props
  maxWidth?:
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl'
    | 'full'
    | string;
  centerContent?: boolean;
  // Layout props with full names
  width?: ChakraContainerProps['width'];
  minWidth?: ChakraContainerProps['minWidth'];
  padding?: ChakraContainerProps['padding'];
  paddingTop?: ChakraContainerProps['paddingTop'];
  paddingRight?: ChakraContainerProps['paddingRight'];
  paddingBottom?: ChakraContainerProps['paddingBottom'];
  paddingLeft?: ChakraContainerProps['paddingLeft'];
  paddingX?: ChakraContainerProps['paddingX'];
  paddingY?: ChakraContainerProps['paddingY'];
  margin?: ChakraContainerProps['margin'];
  marginTop?: ChakraContainerProps['marginTop'];
  marginRight?: ChakraContainerProps['marginRight'];
  marginBottom?: ChakraContainerProps['marginBottom'];
  marginLeft?: ChakraContainerProps['marginLeft'];
  marginX?: ChakraContainerProps['marginX'];
  marginY?: ChakraContainerProps['marginY'];
}

/**
 * Container is a layout component that constrains content width and centers it.
 *
 * @example
 * <Container maxWidth="lg" padding={4}>
 *   <Heading>Welcome</Heading>
 *   <Text>This content is constrained to a maximum width.</Text>
 * </Container>
 *
 * @example
 * <Container maxWidth="4xl" centerContent>
 *   <Box>Centered content</Box>
 * </Container>
 */
export const Container = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
  return <ChakraContainer ref={ref} {...props} />;
});

Container.displayName = 'Container';
